import React, { useEffect, useRef, useState } from 'react'
import { dueAgeText } from '../utils/dateFormatting.js'

const SEVERITY_STYLES = {
  overdue: { color: '#c45a5a', fontWeight: 600 },
  today: { color: '#c47c3a', fontWeight: 600 },
  soon: { color: '#c47c3a' },
  normal: { color: 'var(--text-secondary, #888)' },
  stale: { color: 'var(--text-secondary, #888)', fontStyle: 'italic' },
}

/**
 * Displays the smart due/age string for a task. When `onSetDueDate` is
 * supplied and the row is not disabled, the chip becomes a button that
 * opens a small popover with a native date input + clear affordance.
 *
 * Behaviour matrix:
 *   - disabled                    → plain <span> (snapshot mode)
 *   - !disabled, no dueDate, no createdAt → empty
 *   - !disabled, no dueDate, has createdAt → "+ due" empty-state button
 *     (when onSetDueDate provided), else plain <span>
 *   - !disabled, has dueDate or onSetDueDate → button with popover
 */
export function DueAgeDisplay({ task, now, disabled = false, onSetDueDate }) {
  const { text, severity } = dueAgeText(task, now ?? new Date())
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  // Snapshot/disabled mode: no interaction, no popover.
  if (disabled) {
    if (!text) return null
    return (
      <span
        data-severity={severity}
        style={{
          fontSize: 10,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          ...SEVERITY_STYLES[severity],
        }}
      >
        {text}
      </span>
    )
  }

  const interactive = typeof onSetDueDate === 'function'

  // No setter and nothing to show → render nothing.
  if (!interactive && !text) return null

  // Empty-state ("+ due") affordance — only when interactive and no due date.
  if (!task.dueDate && interactive) {
    return (
      <span ref={wrapperRef} style={styles.wrapper}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Set due date"
          style={styles.emptyButton}
        >
          + due
        </button>
        {open && (
          <Popover
            value={null}
            onChange={(value) => {
              onSetDueDate(value)
              setOpen(false)
            }}
            onClose={() => setOpen(false)}
          />
        )}
      </span>
    )
  }

  // We have either a date or no setter. If we have no setter, render plain.
  if (!interactive) {
    return (
      <span
        data-severity={severity}
        style={{
          fontSize: 10,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          ...SEVERITY_STYLES[severity],
        }}
      >
        {text}
      </span>
    )
  }

  return (
    <span ref={wrapperRef} style={styles.wrapper}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-severity={severity}
        aria-label={task.dueDate ? `Change due date (${text})` : 'Set due date'}
        style={{
          ...styles.dateButton,
          ...SEVERITY_STYLES[severity],
        }}
      >
        {text}
      </button>
      {open && (
        <Popover
          value={task.dueDate}
          onChange={(value) => {
            onSetDueDate(value)
            setOpen(false)
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  )
}

function Popover({ value, onChange, onClose }) {
  const inputRef = useRef(null)
  useEffect(() => {
    inputRef.current?.focus()
  }, [])
  return (
    <div role="dialog" aria-label="Due date" style={styles.popover}>
      <input
        ref={inputRef}
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        aria-label="Due date"
        style={styles.input}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label="Clear due date"
          style={styles.clearBtn}
        >
          Clear
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={styles.closeBtn}
      >
        ×
      </button>
    </div>
  )
}

const styles = {
  wrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  emptyButton: {
    fontSize: 9,
    padding: '1px 6px',
    borderRadius: 10,
    border: '1px dashed var(--border, #ddd)',
    background: 'transparent',
    color: 'var(--color-text-tertiary, #aaa)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dateButton: {
    fontSize: 10,
    padding: '1px 6px',
    borderRadius: 10,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    background: 'var(--surface, #fff)',
    border: '1px solid var(--border, #e8e8e6)',
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    padding: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    zIndex: 20,
  },
  input: {
    fontSize: 12,
    padding: '4px 6px',
    border: '1px solid var(--border, #e8e8e6)',
    borderRadius: 4,
    background: 'var(--surface, #fff)',
    color: 'var(--text-primary)',
  },
  clearBtn: {
    fontSize: 11,
    padding: '4px 8px',
    borderRadius: 4,
    border: '1px solid var(--border, #e8e8e6)',
    background: 'transparent',
    color: 'var(--text-secondary, #666)',
    cursor: 'pointer',
  },
  closeBtn: {
    fontSize: 14,
    padding: '0 4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary, #888)',
    lineHeight: 1,
  },
}
